import { Icon } from '@zambezi/sdk/icons';
import classNames from 'classnames';
import React, { HTMLProps } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import ExternalLink from '../ExternalLink';
import './Button.scss';

export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonVariant =
  | 'primary'
  | 'primary-alternate'
  | 'secondary'
  | 'secondary-alternate'
  | 'link';

export interface ButtonProps extends HTMLProps<HTMLButtonElement> {
  type?: ButtonType;
  variant?: ButtonVariant;
  icon?: string;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  href?: never;
  isExternal?: never;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  children: React.ReactNode | React.ReactNode[];
  isLoading?: boolean;
  fullWidth?: boolean;
}

export interface ButtonInternalLinkProps extends LinkProps {
  type: never;
  variant?: ButtonVariant;
  icon?: string;
  iconPosition?: 'left' | 'right';
  href: string;
  isExternal?: false | never;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  className?: string;
  children: React.ReactNode | React.ReactNode[];
  isLoading?: boolean;
  fullWidth?: boolean;
}

export interface ButtonExternalLinkProps extends Omit<HTMLProps<HTMLAnchorElement>, 'href'> {
  type?: never;
  variant?: ButtonVariant;
  icon?: string;
  iconPosition?: 'left' | 'right';
  href: string;
  isExternal?: true;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  className?: string;
  children: React.ReactNode | React.ReactNode[];
  isLoading?: boolean;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps | ButtonInternalLinkProps | ButtonExternalLinkProps> = ({
  type = 'button',
  variant = 'primary',
  icon,
  iconPosition = 'right',
  href,
  children,
  isExternal,
  disabled,
  onClick,
  className,
  isLoading = false,
  fullWidth = false,
  ...rest
}) => {
  const classes = classNames('zb-button mss-button', className, {
    'zb-button-primary': variant === 'primary',
    'zb-button-secondary': variant === 'secondary',
    'zb-button-primary-alternate': variant === 'primary-alternate',
    'zb-button-secondary-alternate': variant === 'secondary-alternate',
    'zb-button-link': variant === 'link',
    'zb-button-with-icon-after': icon && iconPosition === 'right',
    'zb-button-with-icon-before': icon && iconPosition === 'left',
    'zb-button-is-disabled': disabled,
    'zb-button-is-loading': isLoading,
    'mss-button-full': fullWidth
  });

  const renderChildren = () => (
    <>
      {icon && iconPosition === 'left' && <Icon name={icon} title="" />}
      {children}
      {icon && iconPosition === 'right' && <Icon name={icon} title="" />}
      {isLoading && (
        <span
          className={classNames('zb-loader', {
            'zb-loader-inverted': isLoading && ['primary', 'secondary-alternate'].includes(variant)
          })}
        />
      )}
    </>
  );

  if (href && !isExternal) {
    return (
      <Link to={href} className={classes} {...(rest as LinkProps)} onClick={onClick}>
        {renderChildren()}
      </Link>
    );
  }

  if (href && isExternal) {
    return (
      <ExternalLink
        href={href}
        className={classes}
        {...(rest as HTMLProps<HTMLAnchorElement>)}
        onClick={onClick}
      >
        {renderChildren()}
      </ExternalLink>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={classes}
      disabled={isLoading || disabled}
      {...(rest as ButtonProps)}
    >
      {renderChildren()}
    </button>
  );
};

export default Button;
